import React from 'react';
import { css } from '@emotion/react';
import { i18n_episodes } from '@yi/i18n/phrases/i18n_episodes';

import { Link } from 'gatsby';
import { isRecentlyPublished } from '~/src/common/content';
import { getOptimizedImgSrc } from '~/src/common/image';
import {
  getAllSeriesItemUrl,
  getAllSeriesThumbImgSrc,
  AllSeriesItem,
  getSeriesTitle,
  getSeriesTotalEpisodes,
  getAnimatedCardImageWidth,
} from '~/src/common/lean-back';
import { CardNewChip } from '~/src/components/cards/CardNewChip';
import { useLocale } from '~/src/components/LocaleProvider';
import { fontWeight } from '~/styles';

import { CARD_WIDTH, dimension } from './constants';
import { cardAnimation } from '../elements';
import { Title } from '../Title';

interface Props {
  item: AllSeriesItem;
}

const styles = {
  link: css(cardAnimation, {
    transition: '0.1s ease-in-out',
    width: `${CARD_WIDTH}px`,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    '&:hover': {
      textDecoration: 'none',
    },
  }),
  img: css({
    width: '100%',
    height: `${dimension.height}px`,
    boxShadow: 'rgba(0, 0, 0, 0.16) 0px 8px 16px 0px',
    borderRadius: '10px',
  }),
  totalEpisodes: css({
    fontWeight: `${fontWeight.medium}`,
    fontSize: '14px',
  }),
  title: css({
    marginTop: '8px',
  }),
  newChip: css({
    top: '12px',
    left: '0',
  }),
};

export const SeriesCard: React.FC<Props> & { dimension: { width: number } } = ({ item }) => {
  const { locale } = useLocale();
  const imageWidth = getAnimatedCardImageWidth(CARD_WIDTH);

  const isNew = isRecentlyPublished(item);
  return (
    <Link to={getAllSeriesItemUrl(item)} aria-label="link to series" css={styles.link}>
      {isNew && <CardNewChip css={styles.newChip} />}
      <img
        css={styles.img}
        src={getOptimizedImgSrc({ src: getAllSeriesThumbImgSrc(item), width: imageWidth })}
      />
      <Title css={styles.title}>{getSeriesTitle(item)}</Title>
      <div css={styles.totalEpisodes}>{`${getSeriesTotalEpisodes(item)} ${i18n_episodes(
        locale,
      )}`}</div>
    </Link>
  );
};
SeriesCard.dimension = {
  width: CARD_WIDTH,
};
